import { getDefaultRendererUrl } from "@/constants/etr/index";
import { FrameConnector, print, renderDocument } from "@govtechsg/decentralized-renderer-react-components";
import moment from "moment";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import PropTypes from "prop-types";
import { feature, features } from '@/utils/feature-verifier';
import { getEnvAndDex } from "@/utils/get-env-and-dex";

const SCROLLBAR_WIDTH = 20;

const { env } = getEnvAndDex();

const IframeDetails = forwardRef(({ seTTFileName = () => { }, iframeRef, payload, dataElementId }, ref) => {
	const ttData = useSelector((state) => state.tt);

	const toFrame = useRef();
	const [height, setHeight] = useState(250);

	// Get Profile Document Logo
	const { profile } = useSelector(state => state.auth);
	const logoID = profile?.organization?.documentLogo?.key;
	const rendererURL = getDefaultRendererUrl(env) + (feature(features.ETR_LOGO_RENDERER) ? `?logoID=${logoID}` : "");

	const { docData: document, renderUrl: source } = useMemo(() => {
		if(ttData?.data) {
			return { ...ttData.data, renderUrl: ttData.data.renderUrl ?? rendererURL };
		} else if (payload && dataElementId) {
			return {
				docData: {
					"@context": [],
					type: ["VerifiableCredential", "OpenAttestationCredential"],
					issuanceDate: moment().toISOString(),
					issuer: {
						id: "https://example.com",
						name: "DEMO TOKEN REGISTRY",
					},
					openAttestationMetadata: {
						template: {
							type: "EMBEDDED_RENDERER",
							name: dataElementId?.toUpperCase(),
							url: getDefaultRendererUrl(),
						},
						proof: {
							type: "OpenAttestationProofMethod",
							method: "TOKEN_REGISTRY",
							value: "0x1234567890123456789012345678901234567890",
						},
						identityProof: {
							type: "DNS-TXT",
							identifier: "demo-tradetrust.openattestation.com",
						},
					},
					credentialSubject: payload,
				},
				renderUrl: rendererURL,
			};
		}
		return {};
	}, [ttData, payload, dataElementId]);


	const updateTemplates = useCallback((templateList) => {
		const templatesModified = templateList.filter((item) => {
			return (
				item.type === "custom-template" ||
				item.type === "application/pdf" ||
				!item.type
			);
		});

		seTTFileName(templatesModified?.[0]?.label);
	}, []);

	const dispatch = (action) => {
		if (action.type === "UPDATE_HEIGHT") {
			setHeight(action.payload + SCROLLBAR_WIDTH);
		}
		if (action.type === "UPDATE_TEMPLATES") {
			updateTemplates(action.payload);
		}
		if (action.type === "PRINT") {
			if (iframeRef.current?.print) {
				iframeRef.current.print();
			}
		}
	};

	const handleButtonClick = () => {
		if (toFrame.current) {
			toFrame.current(print());
		}
	};

	useImperativeHandle(ref, () => {
		return {
			handlePrint: handleButtonClick,
		};
	}, []);

	const onConnected = useCallback(
		(frame) => {
			toFrame.current = frame;
			toFrame.current(renderDocument({ document }));
		},
		[document, iframeRef]
	);

	return (
		<div className="container_iframe_details" data-testid="container_iframe_details" id="tt-iframe-details">
			{(source) && (
				<FrameConnector
					style={{ height: `${height}px`, width: "100%", border: "0px" }}
					source={source}
					dispatch={dispatch}
					onConnected={onConnected}
				/>
			)}

			{(!source) && (
				<div className="attachment_viewer_default_div" data-testid="attachment_viewer_default_div">
					No preview available for this file format
				</div>
			)}
		</div>
	);
});

IframeDetails.propTypes = {
	seTTFileName: PropTypes.func,
	iframeRef: PropTypes.object,
	payload: PropTypes.object,
	dataElementId: PropTypes.string,
};

export default IframeDetails;
